import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatStepper, MatStep, MatStepLabel } from '@angular/material/stepper';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UnivPrefsService } from '_services/univ-prefs.service';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Preferences } from 'app/_models/preferences';
import { PublishingService } from '_services/publishing.service';
import { HelpComponent } from '../../_shared/help/help.component';
import { AsyncPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatInput, MatError, MatHint, MatSuffix } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { COHORT_YEARS } from '../../_shared/aid-year/aid-year.config';

type StepKey = 'school-name' | 'logo-banner' | 'welcome-page' | 'cohort-phrase';

@Component({
    selector: 'app-onboarding',
    templateUrl: './onboarding.component.html',
    styleUrls: ['./onboarding.component.css'],
    imports: [HelpComponent, MatButton, MatStepper, MatStep, MatStepLabel, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatError, MatHint, MatSelect, MatOption, MatSuffix, AsyncPipe]
})
export class OnboardingComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatStepper) stepper?: MatStepper;
  @ViewChildren('stepHeading') stepHeadings = new QueryList<ElementRef<HTMLHeadingElement>>();

  readonly stepOrder: StepKey[] = ['school-name', 'logo-banner', 'welcome-page', 'cohort-phrase'];

  schoolNameForm = this.fb.group({
    universityName: ['', Validators.required],
    shortName: ['', [Validators.required, Validators.maxLength(20)]]
  });
  brandingForm = this.fb.group({
    headerColor: ['', Validators.required]
  });
  cohortForm = this.fb.group({
    npcExitUrl: [''],
    showEFC: [false],
    cohortAidPercentYear: ['', Validators.required],
    cohortAidPercent: ['', [Validators.required, Validators.min(0), Validators.max(100)]]
  });
  logoSize = new BehaviorSubject<{ width: number; height: number; } | null>(null);
  welcomeSize = new BehaviorSubject<{ width: number; height: number; } | null>(null);

  currentStepIndex = 0;
  savingStep: StepKey | null = null;
  uploadingStep: 'logo' | 'welcome' | null = null;
  statusMessage = '';
  errorMessage = '';
  isApplyingDraft = false;
  isResettingDraft = false;
  headerColorLocked = false;
  readonly cohortYears = COHORT_YEARS;

  get legacyCohortYear(): string | null {
    const year = this.cohortForm?.get('cohortAidPercentYear')?.value;
    return year && !this.cohortYears.includes(year) ? year : null;
  }

  private queryParamUpdating = false;
  private suppressStepperEvent = false;
  private subscriptions: Subscription[] = [];
  private lastSyncedValues: Partial<Record<StepKey, Record<string, unknown>>> = {};
  constructor(
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    public ups: UnivPrefsService,
    private publishingService: PublishingService,
    private announcer: LiveAnnouncer,
    private host: ElementRef<HTMLElement>
  ) { }

  ngOnInit(): void {
    const initialStep = this.parseStepParam(this.route.snapshot.queryParamMap.get('step'));
    this.currentStepIndex = initialStep;
    this.handlePreferencesStream();
    this.handleQueryParamChanges();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.setStepperIndex(this.currentStepIndex);
      this.focusStepHeading(this.currentStepIndex);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  async onStepperSelectionChange(event: StepperSelectionEvent): Promise<void> {
    if (this.suppressStepperEvent) {
      return;
    }
    const targetIndex = event.selectedIndex;
    if (targetIndex === event.previouslySelectedIndex) {
      return;
    }
    const canLeave = await this.ensureStepCanLeave(this.stepOrder[event.previouslySelectedIndex]);
    if (!canLeave) {
      this.keepStepperAt(event.previouslySelectedIndex);
      return;
    }
    this.currentStepIndex = targetIndex;
    this.navigateWithStep(targetIndex);
  }

  async goToNext(): Promise<void> {
    await this.attemptStepChange(this.currentStepIndex + 1);
  }

  async goToPrevious(): Promise<void> {
    await this.attemptStepChange(this.currentStepIndex - 1);
  }
  async saveCohortStep(): Promise<void> {
    await this.saveIfNeeded(
      'cohort-phrase',
      this.cohortForm,
      () => ({
        npcExitUrl: this.cohortForm.value.npcExitUrl,
        showEFC: this.cohortForm.value.showEFC,
        cohortAidPercentYear: this.cohortForm.value.cohortAidPercentYear,
        cohortAidPercent: this.cohortForm.value.cohortAidPercent
      }),
      'NPC settings',
      true
    );
  }

  async makeChangesLive(): Promise<void> {
    if (this.isApplyingDraft || !this.ups.draftChanges.value) {
      return;
    }
    const schoolId = this.route.snapshot.parent?.paramMap.get('schoolId') ?? '';
    this.isApplyingDraft = true;
    this.errorMessage = '';
    this.statusMessage = 'Applying draft settings to live...';
    this.announcer.announce('Applying draft settings to live.');
    try {
      await this.publishingService.publishDraft(schoolId);
      this.statusMessage = 'Draft settings are now live.';
      this.announcer.announce('Draft settings are now live.');
    } catch (error) {
      console.error('Failed to apply draft settings to live', error);
      this.errorMessage = 'Unable to make draft settings live. Please try again.';
      this.statusMessage = 'Action failed.';
      this.announcer.announce('Unable to make draft settings live.');
    } finally {
      this.isApplyingDraft = false;
    }
  }

  async resetDraftToLive(): Promise<void> {
    if (this.isResettingDraft) {
      return;
    }
    const schoolId = this.route.snapshot.parent?.paramMap.get('schoolId') ?? '';
    this.isResettingDraft = true;
    this.errorMessage = '';
    this.statusMessage = 'Restoring live settings to draft...';
    this.announcer.announce('Restoring live settings to draft.');
    try {
      await this.publishingService.copyToDraft(schoolId);
      // copyToDraft triggers a full reload when successful.
    } catch (error) {
      console.error('Failed to reset draft settings', error);
      this.errorMessage = 'Unable to reset draft settings. Please try again.';
      this.statusMessage = 'Action failed.';
      this.announcer.announce('Unable to reset draft settings.');
    } finally {
      this.isResettingDraft = false;
    }
  }

  get canGoBack(): boolean {
    return this.currentStepIndex > 0;
  }

  get canGoNext(): boolean {
    return this.currentStepIndex < this.stepOrder.length - 1;
  }

  async attemptStepChange(targetIndex: number): Promise<boolean> {
    if (targetIndex < 0 || targetIndex >= this.stepOrder.length) {
      return false;
    }
    if (targetIndex === this.currentStepIndex) {
      return true;
    }
    const currentKey = this.stepOrder[this.currentStepIndex];
    const canLeave = await this.ensureStepCanLeave(currentKey);
    if (!canLeave) {
      return false;
    }
    this.currentStepIndex = targetIndex;
    this.navigateWithStep(targetIndex);
    return true;
  }

  uploadFile(event: Event, type: 'logo' | 'header' | 'page' | 'welcome'): void {
    if (!(event.target instanceof HTMLInputElement) || !event.target.files?.length) {
      return;
    }
    const file = event.target.files.item(0);
    if (!file) {
      return;
    }
    event.target.value = '';
    const sizeKb = Math.round(file.size / 1000);
    if (type === 'welcome' && sizeKb > 2000) {
      alert('The file you are uploading is roughly ' + sizeKb + 'k. Please use squoosh.app to get your file under 2000K');
      return;
    } else if (type !== 'welcome' && sizeKb > 500) {
      alert('The file you are uploading is roughly ' + sizeKb + 'k. Please use squoosh.app to get your file under 500K');
      return;
    }

    const schoolId = this.route.snapshot.parent?.paramMap.get('schoolId') ?? '';
    this.uploadingStep = type === 'welcome' ? 'welcome' : 'logo';
    this.statusMessage = 'Uploading ' + (type === 'welcome' ? 'welcome image' : 'logo') + '...';
    this.errorMessage = '';
    this.ups.uploadImageFile(schoolId, file, type).subscribe({
      next: result => {
        if (!result.success) {
          this.errorMessage = result.message;
          this.statusMessage = 'Upload failed.';
          this.announcer.announce('Upload failed: ' + result.message);
        } else {
          this.statusMessage = 'Upload completed.';
          this.announcer.announce('Upload completed for ' + (type === 'welcome' ? 'welcome image' : 'logo'));
        }
      },
      error: err => {
        console.error('Unexpected upload error:', err);
        this.errorMessage = 'An unexpected error occurred during upload. Please try again.';
        this.statusMessage = 'Upload failed.';
        this.announcer.announce('Upload failed due to an unexpected error.');
        this.uploadingStep = null;
      },
      complete: () => {
        this.uploadingStep = null;
        if (event.target instanceof HTMLInputElement) {
          event.target.value = '';
        }
      }
    });
  }

  removeImageRef(type: 'logo' | 'welcome'): void {
    const schoolId = this.route.snapshot.parent?.paramMap.get('schoolId') ?? '';
    this.ups.removeImageRef(schoolId, type);
    if (type === 'logo') {
      this.logoSize.next(null);
    } else {
      this.welcomeSize.next(null);
    }
    this.statusMessage = (type === 'logo' ? 'Logo' : 'Welcome image') + ' removed.';
    this.announcer.announce((type === 'logo' ? 'Logo' : 'Welcome image') + ' removed.');
  }

  storeImageSize(event: Event, type: 'logo' | 'welcome'): void {
    if (!(event.target instanceof HTMLImageElement)) {
      return;
    }
    const size = { width: event.target.naturalWidth, height: event.target.naturalHeight };
    if (type === 'logo') {
      this.logoSize.next(size);
    } else {
      this.welcomeSize.next(size);
    }
  }

  trackByKey(index: number, key: StepKey): StepKey {
    return key;
  }

  get headerColorValue(): string {
    return this.brandingForm?.get('headerColor')?.value ?? '';
  }

  private handlePreferencesStream(): void {
    const sub = this.ups.draftPrefs.pipe(filter((prefs): prefs is Preferences => !!prefs))
      .subscribe(prefs => {
        this.patchSchoolNameForm(prefs);
        this.patchBrandingForm(prefs);
        this.patchCohortForm(prefs);
      });
    this.subscriptions.push(sub);
  }

  private handleQueryParamChanges(): void {
    const sub = this.route.queryParamMap.subscribe(params => {
      const stepParam = params.get('step');
      const targetIndex = this.parseStepParam(stepParam);
      if (this.queryParamUpdating) {
        this.queryParamUpdating = false;
        return;
      }
      if (targetIndex === this.currentStepIndex) {
        return;
      }
      this.discardUnsavedChanges(this.stepOrder[this.currentStepIndex]);
      this.currentStepIndex = targetIndex;
      this.setStepperIndex(targetIndex);
      this.focusStepHeading(targetIndex);
    });
    this.subscriptions.push(sub);
  }

  private parseStepParam(stepParam: string | null): number {
    const parsed = stepParam === null ? NaN : Number(stepParam);
    if (!isNaN(parsed) && parsed >= 0 && parsed < this.stepOrder.length) {
      return Math.floor(parsed);
    }
    return 0;
  }

  private async ensureStepCanLeave(step: StepKey): Promise<boolean> {
    if (this.savingStep === step) {
      this.statusMessage = 'Saving in progress. Please wait.';
      this.announcer.announce('Save in progress. Please wait.');
      return false;
    }
    // Only block navigation if in the middle of an upload for the step that includes a file action.
    // In the onboarding flow (per the HTML structure), 'logo-banner' and 'welcome-page' are the steps where uploading occurs.
    if (
      (this.uploadingStep === 'logo' && step === 'logo-banner') ||
      (this.uploadingStep === 'welcome' && step === 'welcome-page')
    ) {
      this.errorMessage = 'Please wait for the upload to complete before leaving this step.';
      this.statusMessage = 'Upload in progress.';
      this.announcer.announce('Upload in progress. Please wait.');
      return false;
    }
    switch (step) {
      case 'school-name':
        // Step 1: General university name settings
        return this.saveIfNeeded(
          step,
          this.schoolNameForm,
          () => ({
            universityName: this.schoolNameForm.value.universityName,
            shortName: this.schoolNameForm.value.shortName
          }),
          'School name'
        );
      case 'logo-banner':
        // Step 2: Branding; currently only headerColor is tracked in this form
        return this.saveIfNeeded(
          step,
          this.brandingForm,
          () => ({
            headerColor: this.brandingForm.getRawValue().headerColor
          }),
          'Branding'
        );
      case 'welcome-page':
        // Step 3: No form-backed fields remain on this step, always permit navigation
        return true;
      case 'cohort-phrase':
        // Step 4: Save NPC details (cohort year and percent) collected on the cohort form
        return this.saveIfNeeded(
          step,
          this.cohortForm,
          () => ({
            npcExitUrl: this.cohortForm.value.npcExitUrl,
            showEFC: this.cohortForm.value.showEFC,
            cohortAidPercentYear: this.cohortForm.value.cohortAidPercentYear,
            cohortAidPercent: this.cohortForm.value.cohortAidPercent
          }),
          'NPC settings'
        );
      default:
        return true;
    }
  }

  private async saveIfNeeded(
    step: StepKey,
    form: UntypedFormGroup,
    payloadFactory: () => Partial<Preferences>,
    label: string,
    forceSave = false
  ): Promise<boolean> {
    if (form.invalid) {
      form.markAllAsTouched();
      this.focusFirstInvalidControl(form);
      this.errorMessage = 'Fix the highlighted fields before continuing.';
      this.statusMessage = label + ' contains errors.';
      this.announcer.announce(label + ' contains errors. Fix them before continuing.');
      return false;
    }
    if (!form.dirty && !forceSave) {
      return true;
    }
    return this.persistStep(step, payloadFactory, form, label);
  }

  private async persistStep(
    step: StepKey,
    payloadFactory: () => Partial<Preferences>,
    form: UntypedFormGroup,
    label: string
  ): Promise<boolean> {
    try {
      this.savingStep = step;
      this.errorMessage = '';
      this.statusMessage = 'Saving ' + label.toLowerCase() + '...';
      this.announcer.announce('Saving ' + label.toLowerCase());
      await this.ups.savePreferences(payloadFactory(), true);
      form.markAsPristine();
      this.lastSyncedValues[step] = { ...form.getRawValue() };
      this.statusMessage = label + ' saved.';
      this.announcer.announce(label + ' saved.');
      return true;
    } catch (error) {
      console.error('Failed to save ' + step + ' step', error);
      this.errorMessage = 'Unable to save ' + label.toLowerCase() + '. Please try again.';
      this.statusMessage = 'Save failed.';
      this.announcer.announce('Unable to save ' + label.toLowerCase());
      return false;
    } finally {
      this.savingStep = null;
    }
  }

  private navigateWithStep(index: number): void {
    this.queryParamUpdating = true;
    this.setStepperIndex(index);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { step: index },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
    this.focusStepHeading(index);
  }

  private setStepperIndex(index: number): void {
    if (!this.stepper) {
      return;
    }
    this.suppressStepperEvent = true;
    this.stepper.selectedIndex = index;
    this.suppressStepperEvent = false;
  }

  private keepStepperAt(index: number): void {
    if (!this.stepper) {
      return;
    }
    this.suppressStepperEvent = true;
    this.stepper.selectedIndex = index;
    this.suppressStepperEvent = false;
  }

  private focusStepHeading(index: number): void {
    if (!this.stepHeadings) {
      return;
    }
    const heading = this.stepHeadings.get(index);
    if (heading?.nativeElement) {
      heading.nativeElement.focus();
    }
  }

  private patchBrandingForm(prefs: Preferences): void {
    const color = prefs.headerColor || '#0b2a72';
    this.brandingForm.patchValue({ headerColor: color }, { emitEvent: false });
    this.updateHeaderColorAvailability(prefs);
    this.brandingForm.markAsPristine();
    this.lastSyncedValues['logo-banner'] = { ...this.brandingForm.getRawValue() };
  }

  private patchSchoolNameForm(prefs: Preferences): void {
    this.schoolNameForm.patchValue({
      universityName: prefs.universityName || '',
      shortName: prefs.shortName || ''
    }, { emitEvent: false });
    this.schoolNameForm.markAsPristine();
    this.lastSyncedValues['school-name'] = { ...this.schoolNameForm.getRawValue() };
  }

  private patchCohortForm(prefs: Preferences): void {
    this.cohortForm.patchValue({
      npcExitUrl: prefs.npcExitUrl || '',
      showEFC: prefs.showEFC ?? false,
      cohortAidPercentYear: prefs.cohortAidPercentYear || '',
      cohortAidPercent: prefs.cohortAidPercent || ''
    }, { emitEvent: false });
    this.cohortForm.markAsPristine();
    this.lastSyncedValues['cohort-phrase'] = { ...this.cohortForm.getRawValue() };
  }

  private discardUnsavedChanges(step: StepKey): void {
    const lastValues = this.lastSyncedValues[step];
    const form = this.getFormForStep(step);
    if (form && form.dirty) {
      if (lastValues) {
        form.reset(lastValues);
      } else {
        form.reset();
      }
      form.markAsPristine();
      this.statusMessage = 'Unsaved changes for this step were discarded.';
      this.announcer.announce('Unsaved changes were discarded.');
    }
  }

  private getFormForStep(step: StepKey): UntypedFormGroup | null {
    switch (step) {
      case 'school-name':
        return this.schoolNameForm;
      case 'logo-banner':
        return this.brandingForm;
      case 'cohort-phrase':
        return this.cohortForm;
      default:
        return null;
    }
  }

  private focusFirstInvalidControl(form: UntypedFormGroup): void {
    const invalidName = Object.keys(form.controls).find(key => form.controls[key].invalid);
    if (!invalidName) {
      return;
    }
    const element = this.host.nativeElement.querySelector<HTMLElement>('[formcontrolname="' + invalidName + '"]');
    if (element) {
      element.focus();
    }
  }

  private updateHeaderColorAvailability(prefs: Preferences): void {
    const headerType = prefs.headerType ?? 'hSolid';
    const isSolidColor = headerType === 'hSolid';
    this.headerColorLocked = !isSolidColor;
    const control = this.brandingForm.get('headerColor');
    if (!control) {
      return;
    }
    if (this.headerColorLocked) {
      control.disable({ emitEvent: false });
    } else {
      control.enable({ emitEvent: false });
    }
  }

}
